import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CODE_ICON, COLLECTION_ICON, LEADERBOARD_ICON, actionIcons } from '../utils/iconMap';
import { t } from '../locales';

const CloseIcon = actionIcons.x;

/** Sürüm: içerik değişirse anahtarı güncelle; eski kullanıcılar yeni rehberi bir kez daha görebilir. */
export const DASHBOARD_QUICK_GUIDE_STORAGE_KEY = 'dekosta_dashboard_quick_guide_v1';

function readDismissed() {
  try {
    return typeof localStorage !== 'undefined' && localStorage.getItem(DASHBOARD_QUICK_GUIDE_STORAGE_KEY) === '1';
  } catch {
    return true;
  }
}

/**
 * Dashboard hero altı — tek seferlik, kapatılınca localStorage ile kalıcı gizlenir.
 */
export default function DashboardQuickGuide() {
  const navigate = useNavigate();
  const [visible, setVisible] = useState(() => !readDismissed());

  const dismiss = useCallback(() => {
    try {
      localStorage.setItem(DASHBOARD_QUICK_GUIDE_STORAGE_KEY, '1');
    } catch {
      /* ignore quota / private mode */
    }
    setVisible(false);
  }, []);

  const goActivate = useCallback(() => {
    navigate('/activate');
  }, [navigate]);

  if (!visible) return null;

  const steps = [
    {
      key: 'code',
      icon: CODE_ICON,
      label: t('activation.quickGuide.stepEnterCode'),
      onClick: goActivate,
      interactive: true,
    },
    {
      key: 'earn',
      icons: [actionIcons.userCircle, COLLECTION_ICON],
      label: t('activation.quickGuide.stepEarn'),
      interactive: false,
    },
    {
      key: 'grow',
      icon: LEADERBOARD_ICON,
      label: t('activation.quickGuide.stepGrow'),
      interactive: false,
    },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
      className="relative rounded-2xl border border-white/[0.08] bg-zinc-950/60 backdrop-blur-md shadow-[inset_0_1px_0_0_rgba(255,255,255,0.04)] overflow-hidden"
      data-testid="dashboard-quick-guide"
    >
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-violet-500/25 to-transparent pointer-events-none" aria-hidden />
      <button
        type="button"
        onClick={dismiss}
        className="absolute top-2 right-2 z-10 p-1.5 rounded-lg text-zinc-500 hover:text-zinc-200 hover:bg-white/[0.06] transition-colors touch-manipulation"
        aria-label={t('activation.quickGuide.dismissAria')}
        data-testid="dashboard-quick-guide-dismiss"
      >
        <CloseIcon className="w-4 h-4" strokeWidth={2} aria-hidden />
      </button>

      <div className="px-3 pt-9 pb-3 sm:px-4 sm:pt-3 sm:pb-3.5 sm:pr-11">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5 sm:gap-3">
          {steps.map((step, i) => {
            const n = i + 1;
            const baseClass =
              'flex items-start gap-2.5 rounded-xl px-2.5 py-2 sm:py-2.5 border border-transparent text-left transition-colors';
            const inner = (
              <>
                <span
                  className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-violet-950/80 border border-violet-500/25 text-[11px] font-bold tabular-nums text-violet-200"
                  aria-hidden
                >
                  {n}
                </span>
                <div className="min-w-0 flex-1 pt-0.5">
                  <div className="flex items-center gap-1.5 text-violet-300/90 mb-0.5">
                    {step.icons
                      ? step.icons.map((Icon, idx) => (
                          <Icon key={idx} className="w-3.5 h-3.5 shrink-0" strokeWidth={2.25} aria-hidden />
                        ))
                      : step.icon &&
                        React.createElement(step.icon, {
                          className: 'w-3.5 h-3.5 shrink-0',
                          strokeWidth: 2.25,
                          'aria-hidden': true,
                        })}
                  </div>
                  <p className="text-[11px] sm:text-xs text-zinc-400 leading-snug">{step.label}</p>
                </div>
              </>
            );

            if (step.interactive) {
              return (
                <button
                  key={step.key}
                  type="button"
                  onClick={step.onClick}
                  className={`${baseClass} w-full hover:border-violet-500/20 hover:bg-violet-950/25 active:bg-violet-950/35 cursor-pointer touch-manipulation`}
                >
                  {inner}
                </button>
              );
            }

            return (
              <div key={step.key} className={`${baseClass} bg-black/15`}>
                {inner}
              </div>
            );
          })}
        </div>
      </div>
    </motion.div>
  );
}
